import { ArrowUpRight, Ear, Focus, Route } from "lucide-react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";

const PRINCIPLES = [
  {
    icon: Ear,
    title: "Escutar a operação",
    text: "Entender pessoas, rotinas e decisões antes de propor qualquer tecnologia.",
  },
  {
    icon: Focus,
    title: "Encontrar o ponto certo",
    text: "Separar o que apenas ocupa tempo daquilo que realmente impede o negócio de avançar.",
  },
  {
    icon: Route,
    title: "Construir com direção",
    text: "Transformar entendimento em uma solução simples de usar e preparada para evoluir.",
  },
];

export const Manifesto = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  const firstLineX = useTransform(scrollYProgress, [0, 0.42, 1], [48, 0, 20]);
  const secondLineX = useTransform(scrollYProgress, [0, 0.42, 1], [16, 0, 24]);
  const railX = useTransform(scrollYProgress, [0, 1], ["-12%", "2%"]);

  return (
    <section ref={ref} id="manifesto" data-header-theme="light" className="overflow-hidden bg-[#f1f3ec] pt-24 text-[#050606] md:pt-36" data-testid="manifesto-section">
      <div className="site-shell">
        <div className="flex items-center justify-between gap-8 border-b border-black/15 pb-6">
          <p className="eyebrow !text-black/55">Nosso ponto de partida</p>
          <p className="hidden text-[9px] font-bold uppercase tracking-[.19em] text-black/32 sm:block">Estratégia antes da execução</p>
        </div>

        <div className="py-16 md:py-24">
          <div className="overflow-hidden py-1">
            <motion.h2 className="display-title text-[clamp(3.25rem,8.6vw,9rem)] leading-[.91]" style={reduce ? undefined : { x: firstLineX }}>
              Código é consequência.
            </motion.h2>
          </div>
          <div className="overflow-hidden py-1">
            <motion.p className="display-title text-[clamp(3.25rem,8.6vw,9rem)] leading-[.91] text-black/34" style={reduce ? undefined : { x: secondLineX }}>
              Clareza é o começo.
            </motion.p>
          </div>
        </div>

        <div className="grid border-y border-black/15 lg:grid-cols-[.72fr_1.28fr]">
          <div className="flex flex-col justify-between border-b border-black/15 py-9 lg:border-b-0 lg:border-r lg:py-12 lg:pr-12">
            <p className="display-title max-w-xl text-2xl leading-[1.15] sm:text-3xl md:text-4xl">
              Antes de decidir o que construir, precisamos entender o que está impedindo o negócio de avançar.
            </p>
            <div className="mt-10">
              <p className="max-w-md text-sm leading-relaxed text-black/55 md:text-base">
                Tecnologia sob medida não começa com uma lista de funcionalidades. Começa com uma conversa capaz de enxergar o todo.
              </p>
              <Link to="/sobre" className="group mt-8 inline-flex items-center gap-3 border-b border-black/35 pb-2 text-sm font-bold transition-colors hover:border-black">
                Conheça o Clube Digital
                <ArrowUpRight size={17} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>

          <div>
            {PRINCIPLES.map(({ icon: Icon, title, text }) => (
              <article key={title} className="group grid gap-5 border-b border-black/15 py-8 last:border-b-0 sm:grid-cols-[52px_.75fr_1.25fr] sm:items-center sm:px-8 lg:py-10 lg:pl-12 lg:pr-0">
                <span className="flex h-11 w-11 items-center justify-center border border-black/18 transition-colors group-hover:bg-[#050606] group-hover:text-lime-300"><Icon size={18} /></span>
                <h3 className="display-title text-xl sm:text-2xl">{title}</h3>
                <p className="max-w-md text-sm leading-relaxed text-black/52 md:text-base">{text}</p>
              </article>
            ))}
          </div>
        </div>
      </div>

      <div data-header-theme="dark" className="mt-24 overflow-hidden border-t border-black/15 bg-[#050606] py-5 text-[#f1f3ec] md:mt-32 md:py-7" aria-hidden="true">
        <motion.div className="flex w-max items-center gap-8 whitespace-nowrap" style={reduce ? undefined : { x: railX }}>
          {[0, 1].map((copy) => (
            <div key={copy} className="flex items-center gap-8">
              <span className="display-title text-2xl sm:text-4xl">Escutar</span><span className="h-2 w-2 rounded-full bg-lime-300" />
              <span className="display-title text-2xl sm:text-4xl text-white/42">Entender</span><span className="h-2 w-2 rounded-full bg-cyan-300" />
              <span className="display-title text-2xl sm:text-4xl">Simplificar</span><span className="h-2 w-2 rounded-full bg-lime-300" />
              <span className="display-title text-2xl sm:text-4xl text-white/42">Construir</span><span className="h-2 w-2 rounded-full bg-cyan-300" />
              <span className="display-title text-2xl sm:text-4xl">Evoluir</span><span className="h-2 w-2 rounded-full bg-lime-300" />
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
